'use client';

import { Permission } from '@up2stay/up2promo-api-client';
import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { FileText, Library, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { FeedbackTemplateLibraryPicker } from '@/core/components/feedback/feedback-template-library-picker';
import { FEEDBACK_LIST_SURFACE_CLASS } from '@/core/components/feedback/feedback-surface';
import {
  ListEmptyState,
  ListPageBody,
  ListSearchField,
  ListToolbarSurface,
} from '@/core/components/layout';
import { useFeedbackFormTemplates } from '@/core/hooks/use-feedback';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

type FeedbackFormsPanelProps = {
  /** When true, render create actions inside the empty state (header may also own CTAs). */
  showInlineCreate?: boolean;
  /** Controlled library picker — when provided, panel does not own open state. */
  libraryOpen?: boolean;
  onLibraryOpenChange?: (open: boolean) => void;
};

export function FeedbackFormsPanel({
  showInlineCreate = true,
  libraryOpen: libraryOpenProp,
  onLibraryOpenChange,
}: FeedbackFormsPanelProps) {
  const dict = useDictionary().feedback;
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canEdit = can(Permission.FEEDBACK_INVITE);
  const workspaceId = useCurrentWorkspace()?.id;
  const [search, setSearch] = useState('');
  const [internalLibraryOpen, setInternalLibraryOpen] = useState(false);
  const libraryOpen = libraryOpenProp ?? internalLibraryOpen;
  const setLibraryOpen = onLibraryOpenChange ?? setInternalLibraryOpen;

  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useFeedbackFormTemplates(workspaceId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.title.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q),
    );
  }, [search, templates]);

  return (
    <ListPageBody>
      <ListToolbarSurface>
        <ListSearchField
          variant="toolbar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={dict.forms.searchPlaceholder}
          onClear={() => setSearch('')}
        />
      </ListToolbarSurface>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
          <Skeleton className="h-16 w-full rounded-[var(--r-card)]" />
        </div>
      ) : error ? (
        <ListEmptyState
          title={dict.forms.loadFailed}
          action={
            <Button type="button" variant="outline" onClick={() => refetch()}>
              {dict.retry}
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <ListEmptyState
          icon={<FileText className="h-6 w-6 text-muted-foreground" />}
          title={
            search.trim()
              ? dict.forms.empty.noResultsTitle
              : dict.forms.empty.title
          }
          description={
            search.trim()
              ? dict.forms.empty.noResultsDescription
              : dict.forms.empty.description
          }
          action={
            canEdit && showInlineCreate && !search.trim() ? (
              <div className="flex flex-col items-center gap-2 sm:flex-row">
                <Button type="button" onClick={() => setLibraryOpen(true)}>
                  <Library className="size-4" />
                  {dict.forms.fromLibrary}
                </Button>
                <Button
                  type="button"
                  variant="dashed"
                  onClick={() => navigateShell(shellPaths.feedbackFormNew)}
                >
                  <Plus className="size-4" />
                  {dict.forms.createBlank}
                </Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <ul className={FEEDBACK_LIST_SURFACE_CLASS}>
          {filtered.map((template) => (
            <li key={template.id}>
              <button
                type="button"
                className="flex w-full min-h-11 items-start justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
                onClick={() =>
                  navigateShell(shellPaths.feedbackFormDetail(template.id))
                }
              >
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-sm font-semibold tracking-tight">
                    {template.name}
                  </p>
                  <p className="truncate text-[13.5px] text-muted-foreground">
                    {template.title} ·{' '}
                    {dict.forms.questionCount.replace(
                      '{count}',
                      String(template.questions.length),
                    )}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <FeedbackTemplateLibraryPicker
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        kind="form"
        onAdded={(templateId) =>
          navigateShell(shellPaths.feedbackFormDetail(templateId))
        }
      />
    </ListPageBody>
  );
}
